import React, { useState, forwardRef } from 'react'
import { useDispatch } from 'react-redux'
import { targetSetPointer } from '../../redux/actions.js'
import {
  Box,
  Popover,
  Typography,
  MenuItem,
  Select,
  Grid,
  Slider,
  Input,
  Divider,
  Switch,
  Grow,
} from '@material-ui/core'
import Draggable from 'react-draggable'
import Plot from '../custom-plotly.js'

const DraggableWrapper = forwardRef(({ children, ...other }, ref) => {
  return (
    <Draggable ref={ref} handle=".handle">
      {React.cloneElement(children, { ...other })}
    </Draggable>
  )
})

const DraggableGrow = forwardRef(({ children, ...other }, ref) => {
  return (
    <Grow {...other} timeout={0}>
      <DraggableWrapper ref={ref}>{children}</DraggableWrapper>
    </Grow>
  )
})

const Map2d = (props) => {
  const dispatch = useDispatch()

  const d0 = props.data[0]
  let step50 = (d0.zmax - d0.zmin) / 50.0
  let step50_round = 10 ** Math.floor(Math.log10(step50))

  const customRound = (value, step) => Math.round(value / step) * step
  const [anchorEl, setAnchorEl] = useState(null)
  const [colorMap, setColorMap] = useState('Jet')
  const [typeMap, setTypeMap] = useState('heatmap')
  const [isLogScale, setIsLogScale] = useState(false)
  const [zmap, setZmap] = useState(d0.z)
  const [rangeMapMin, setRangeMapMin] = useState(
    customRound(d0.zmin, step50_round)
  )
  const [rangeMapMax, setRangeMapMax] = useState(
    customRound(d0.zmax, step50_round)
  )
  const [rangeFullMapMin, setRangeFullMapMin] = useState(
    customRound(d0.zmin, step50_round)
  )
  const [rangeFullMapMax, setRangeFullMapMax] = useState(
    customRound(d0.zmax, step50_round)
  )
  const [nStepContour, setNStepContour] = useState(10)

  // calculate step for slider and number input
  const stepContour = (rangeFullMapMax - rangeFullMapMin) / nStepContour

  step50 = (rangeFullMapMax - rangeFullMapMin) / 50.0
  step50_round = 10 ** Math.floor(Math.log10(step50))

  const handleSettingsClick = (event) => {
    setAnchorEl(event)
  }

  const handleSettingsClose = (event) => {
    setAnchorEl(null)
  }

  const handleColorMapSelect = (event) => {
    setColorMap(event.target.value)
  }

  const handleTypeMapSelect = (event) => {
    setTypeMap(event.target.value)
  }

  const handleInputNStepContour = (event) => {
    setNStepContour(Number(event.target.value))
  }

  const handleChangeRangeMap = (event, newValue) => {
    setRangeMapMin(newValue[0])
    setRangeMapMax(newValue[1])
  }

  const handleInputRangeMin = (event) => {
    setRangeMapMin(event.target.value)
    if (Number(event.target.value) < Number(rangeFullMapMin)) {
      setRangeFullMapMin(event.target.value)
    }
  }

  const handleInputRangeMax = (event) => {
    setRangeMapMax(event.target.value)
    if (Number(event.target.value) > Number(rangeFullMapMax)) {
      setRangeFullMapMax(event.target.value)
    }
  }

  const handleChangeFullRangeMin = (event) => {
    setRangeFullMapMin(event.target.value)
    if (Number(event.target.value) > Number(rangeMapMin)) {
      setRangeMapMin(event.target.value)
    }
  }

  const handleChangeFullRangeMax = (event) => {
    setRangeFullMapMax(event.target.value)
    if (Number(event.target.value) < Number(rangeMapMax)) {
      setRangeMapMax(event.target.value)
    }
  }

  const handleLogScale = (event) => {
    setIsLogScale(!isLogScale)

    // Treat correctly log scale
    if (!isLogScale) {
      const logzmap = zmap.map((v) =>
        v.map((v) => (v > 0 ? Math.log10(v) : null))
      )
      const zmin =
        rangeMapMin > 0 ? Math.log10(rangeMapMin) : Math.log10(step50 / 10)
      const zmax =
        rangeMapMax > 0 ? Math.log10(rangeMapMax) : Math.log10(step50 / 10)
      step50 = (zmax - zmin) / 50.0
      step50_round = 10 ** Math.floor(Math.log10(step50))
      setZmap(logzmap)
      setRangeMapMin(customRound(zmin, step50_round))
      setRangeMapMax(customRound(zmax, step50_round))

      setRangeFullMapMin(customRound(zmin, step50_round))
      setRangeFullMapMax(customRound(zmax, step50_round))
    } else {
      step50 = (d0.zmax - d0.zmin) / 50.0
      step50_round = 10 ** Math.floor(Math.log10(step50))
      setZmap(d0.z)
      setRangeMapMin(customRound(d0.zmin, step50_round))
      setRangeMapMax(customRound(d0.zmax, step50_round))
      setRangeFullMapMin(customRound(d0.zmin, step50_round))
      setRangeFullMapMax(customRound(d0.zmax, step50_round))
    }
  }

  const handlerPlotClick = (event) => {
    console.log(
      `Clicked point: x: ${event.points[0].x}, y: ${event.points[0].y}`
    )
    dispatch(targetSetPointer({ x: event.points[0].x, y: event.points[0].y }))
  }

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  const dataMap = {
    z: zmap,
    x: d0.x,
    y: d0.y,
    zmin: rangeMapMin,
    zmax: rangeMapMax,
    autocontour: false,
    contours: {
      start: rangeMapMin,
      end: rangeMapMax,
      size: stepContour,
    },
    type: typeMap,
    colorscale: colorMap,
    colorbar: {
      x: 0.95,
      thickness: 10,
      len: 1.1,
      lenmode: 'fraction',
    },
    name: '',
  }

  // if pointer is specified plot it, otherwise show only map
  const data = props.pointXY
    ? [
        dataMap,
        {
          x: [props.pointXY.x],
          y: [props.pointXY.y],
          type: 'scatter',
          mode: 'markers',
          marker: {
            symbol: 'cross',
            color: 'rgba(0, 0, 10, 0.5)',
            size: 8,
            line: {
              color: 'rgb(200, 200, 200)',
              width: 1.0,
            },
          },
          hoverinfo: 'skip',
          showlegend: false,
        },
      ]
    : [dataMap]

  const layout = {
    title: isLogScale ? `log10(${props.title})` : props.title,
    font: { size: 11 },
    xaxis: {
      title: '&#916; X, arcsec',
      scaleanchor: 'y',
      scaleratio: 1,
      range: [props.range[0], props.range[1]],
    },
    yaxis: {
      title: '&#916; Y, arcsec',
      range: [props.range[2], props.range[3]],
    },
    autosize: false,
    height: 250,
    width: 250,
    margin: { l: 30, r: 0, b: 30, t: 30, pad: 0 },
  }

  const config = {
    displaylogo: false,
    modeBarButtonsToRemove: [
      // 'zoom2d',
      // 'pan2d',
      'select2d',
      'lasso2d',
      // 'zoomIn2d',
      // 'zoomOut2d',
      'autoScale2d',
      // 'resetScale2d',
      'hoverClosestGl2d',
      'hoverClosestPie',
      'toggleHover',
      'resetViews',
      // 'toImage',
      'sendDataToCloud',
      'toggleSpikelines',
      'resetViewMapbox',
      'hoverClosestCartesian',
      'hoverCompareCartesian',
    ],
  }

  // Add new button into thee Plotly Menu Bar
  const newButton = {
    name: 'Map settings',
    icon: {
      width: 24,
      height: 24,
      viewBox: [0, 0, 24, 24],
      path:
        'M19.1401 12.9404C19.1801 12.6404 19.2001 12.3304 19.2001 12.0004C19.2001 11.6804 19.1801 11.3604 19.1301 11.0604L21.1601 9.48039C21.3401 9.34039 21.3901 9.07039 21.2801 8.87039L19.3601 5.55039C19.2401 5.33039 18.9901 5.26039 18.7701 5.33039L16.3801 6.29039C15.8801 5.91039 15.3501 5.59039 14.7601 5.35039L14.4001 2.81039C14.3601 2.57039 14.1601 2.40039 13.9201 2.40039H10.0801C9.84011 2.40039 9.65011 2.57039 9.61011 2.81039L9.25011 5.35039C8.66011 5.59039 8.12011 5.92039 7.63011 6.29039L5.24011 5.33039C5.02011 5.25039 4.77011 5.33039 4.65011 5.55039L2.74011 8.87039C2.62011 9.08039 2.66011 9.34039 2.86011 9.48039L4.89011 11.0604C4.84011 11.3604 4.80011 11.6904 4.80011 12.0004C4.80011 12.3104 4.82011 12.6404 4.87011 12.9404L2.84011 14.5204C2.66011 14.6604 2.61011 14.9304 2.72011 15.1304L4.64011 18.4504C4.76011 18.6704 5.01011 18.7404 5.23011 18.6704L7.62011 17.7104C8.12011 18.0904 8.65011 18.4104 9.24011 18.6504L9.60011 21.1904C9.65011 21.4304 9.84011 21.6004 10.0801 21.6004H13.9201C14.1601 21.6004 14.3601 21.4304 14.3901 21.1904L14.7501 18.6504C15.3401 18.4104 15.8801 18.0904 16.3701 17.7104L18.7601 18.6704C18.9801 18.7504 19.2301 18.6704 19.3501 18.4504L21.2701 15.1304C21.3901 14.9104 21.3401 14.6604 21.1501 14.5204L19.1401 12.9404ZM12.0001 15.6004C10.0201 15.6004 8.40011 13.9804 8.40011 12.0004C8.40011 10.0204 10.0201 8.40039 12.0001 8.40039C13.9801 8.40039 15.6001 10.0204 15.6001 12.0004C15.6001 13.9804 13.9801 15.6004 12.0001 15.6004Z',
    },
    direction: 'up',
    click: handleSettingsClick,
  }

  config.modeBarButtonsToAdd = [newButton]

  return (
    <>
      <Plot
        data={data}
        layout={layout}
        config={config}
        onClick={handlerPlotClick}
      />
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        // placement={'right-end'}
        onClose={handleSettingsClose}
        // transition
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        TransitionComponent={DraggableGrow}
      >
        <Box m={2} width={300}>
          <Typography
            variant="h6"
            gutterBottom
            className={'handle'}
            style={{ cursor: 'move' }}
          >
            Map Settings
          </Typography>
          <Divider />
          <Grid
            container
            alignItems="baseline"
            justify="flex-start"
            spacing={2}
          >
            <Grid item>
              <Typography>Plot type:</Typography>
            </Grid>
            <Grid item>
              <Select
                value={typeMap}
                onChange={handleTypeMapSelect}
                // displayEmpty
              >
                <MenuItem value={'heatmap'}>Heatmap</MenuItem>
                <MenuItem value={'contour'}>Contour</MenuItem>
              </Select>
            </Grid>
          </Grid>

          {typeMap === 'contour' && (
            <Grid
              container
              alignItems="baseline"
              justify="flex-start"
              spacing={2}
            >
              <Grid item>
                <Typography>Number of levels:</Typography>
              </Grid>
              <Grid item xs={3}>
                <Input
                  value={nStepContour}
                  margin="dense"
                  onChange={handleInputNStepContour}
                  inputProps={{
                    type: 'number',
                    step: 1,
                    min: 1,
                  }}
                />
              </Grid>
            </Grid>
          )}

          <Grid
            container
            alignItems="baseline"
            justify="flex-start"
            spacing={2}
          >
            <Grid item>
              <Typography>Color map:</Typography>
            </Grid>
            <Grid item>
              <Select
                value={colorMap}
                onChange={handleColorMapSelect}
                // displayEmpty
              >
                <MenuItem value={'Greys'}>Greys</MenuItem>
                <MenuItem value={'YlGnBu'}>YlGnBu</MenuItem>
                <MenuItem value={'Greens'}>Greens</MenuItem>
                <MenuItem value={'YlOrRd'}>YlOrRd</MenuItem>
                <MenuItem value={'Bluered'}>Bluered</MenuItem>
                <MenuItem value={'RdBu'}>RdBu</MenuItem>
                <MenuItem value={'Reds'}>Reds</MenuItem>
                <MenuItem value={'Blues'}>Blues</MenuItem>
                <MenuItem value={'Picnic'}>Picnic</MenuItem>
                <MenuItem value={'Rainbow'}>Rainbow</MenuItem>
                <MenuItem value={'Portland'}>Portland</MenuItem>
                <MenuItem value={'Jet'}>Jet</MenuItem>
                <MenuItem value={'Hot'}>Hot</MenuItem>
                <MenuItem value={'Blackbody'}>Blackbody</MenuItem>
                <MenuItem value={'Earth'}>Earth</MenuItem>
                <MenuItem value={'Electric'}>Electric</MenuItem>
                <MenuItem value={'Viridis'}>Viridis</MenuItem>
                <MenuItem value={'Cividis'}>Cividis</MenuItem>
              </Select>
            </Grid>
          </Grid>

          <Grid
            container
            alignItems="baseline"
            justify="flex-start"
            spacing={2}
          >
            <Grid item>
              <Typography>Logarithmic scale</Typography>
            </Grid>
            <Grid item>
              <Switch
                checked={isLogScale}
                onChange={handleLogScale}
                name="isLogScale"
                color="primary"
              />
            </Grid>
          </Grid>

          <Box my={1}>
            <Divider />
          </Box>

          <Box>
            <Typography gutterBottom>Range</Typography>
            <Slider
              value={[Number(rangeMapMin), Number(rangeMapMax)]}
              min={rangeFullMapMin === '' ? null : Number(rangeFullMapMin)}
              max={rangeFullMapMax === '' ? null : Number(rangeFullMapMax)}
              step={step50_round}
              onChange={handleChangeRangeMap}
              valueLabelDisplay="auto"
            />
            <Grid
              container
              // alignItems="baseline"
              justify="flex-start"
              spacing={2}
            >
              <Grid item xs>
                <Input
                  // value={Math.round(rangeMapMin / step50_round) * step50_round}
                  value={rangeMapMin}
                  margin="dense"
                  onChange={handleInputRangeMin}
                  inputProps={{
                    type: 'number',
                    step: step50_round,
                  }}
                />
              </Grid>
              <Grid item xs>
                <Input
                  // value={Math.round(rangeMapMax / step50_round) * step50_round}
                  value={rangeMapMax}
                  margin="dense"
                  onChange={handleInputRangeMax}
                  inputProps={{
                    type: 'number',
                    step: step50_round,
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          <Box mt={2}>
            <Typography gutterBottom>Full range</Typography>
            <Grid
              container
              // alignItems="baseline"
              justify="flex-start"
              spacing={2}
            >
              <Grid item xs>
                <Input
                  value={rangeFullMapMin}
                  margin="dense"
                  size="small"
                  onChange={handleChangeFullRangeMin}
                  inputProps={{
                    type: 'number',
                    step: step50_round,
                  }}
                />
              </Grid>
              <Grid item xs>
                <Input
                  value={rangeFullMapMax}
                  margin="dense"
                  size="small"
                  onChange={handleChangeFullRangeMax}
                  inputProps={{
                    type: 'number',
                    step: step50_round,
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Popover>
    </>
  )
}

export default Map2d
